'use client';

import { useEffect, useState } from 'react';

/**
 * Banner de consimțământ cookies pentru app.softfactura.ro (ePrivacy + GDPR).
 *
 * Partajează cookie-ul `cookie-consent` pe domeniul `.softfactura.ro` cu
 * marketing-ul -> dacă utilizatorul a ales deja pe softfactura.ro, banner-ul NU
 * reapare aici. Necesar totuşi pentru vizitatorii care intră direct pe
 * app.softfactura.ro/register sau /login. Actualizează Google Consent Mode v2.
 *
 * Politica de cookies live pe marketing (softfactura.ro/cookies).
 */

const CONSENT_COOKIE = 'cookie-consent';
const ONE_YEAR = 60 * 60 * 24 * 365;
const OPEN_EVENT = 'sf:open-cookie-preferences';

type Gtag = (...args: unknown[]) => void;

function cookieDomainSuffix(): string {
  if (typeof window === 'undefined') return '';
  return window.location.hostname.endsWith('softfactura.ro') ? '; domain=.softfactura.ro' : '';
}

function readConsent(): { analytics: boolean } | null {
  if (typeof document === 'undefined') return null;
  const raw = document.cookie.match(/(?:^|; )cookie-consent=([^;]*)/)?.[1];
  if (!raw) return null;
  try {
    const c = JSON.parse(decodeURIComponent(raw));
    return { analytics: !!c.analytics };
  } catch {
    return null;
  }
}

function saveConsent(analytics: boolean): void {
  const value = encodeURIComponent(JSON.stringify({ analytics, marketing: false, v: 1 }));
  document.cookie = `${CONSENT_COOKIE}=${value}; path=/${cookieDomainSuffix()}; max-age=${ONE_YEAR}; SameSite=Lax`;
  const gtag = (window as unknown as { gtag?: Gtag }).gtag;
  if (typeof gtag === 'function') {
    gtag('consent', 'update', { analytics_storage: analytics ? 'granted' : 'denied' });
  }
}

const btnPrimary =
  'rounded-full bg-primary-600 px-5 py-2 text-sm font-semibold text-white transition-colors hover:bg-primary-700';
const btnSecondary =
  'rounded-full border border-neutral-300 px-5 py-2 text-sm font-medium text-neutral-700 transition-colors hover:bg-neutral-50 dark:border-neutral-600 dark:text-neutral-200 dark:hover:bg-neutral-800';
const btnGhost =
  'rounded-full px-4 py-2 text-sm font-medium text-neutral-500 transition-colors hover:text-neutral-900 dark:text-neutral-400 dark:hover:text-neutral-100';

export function CookieConsent() {
  const [visible, setVisible] = useState(false);
  const [customize, setCustomize] = useState(false);
  const [analytics, setAnalytics] = useState(true);

  useEffect(() => {
    if (!readConsent()) setVisible(true);

    const onOpen = () => {
      const c = readConsent();
      setAnalytics(c ? c.analytics : true);
      setCustomize(true);
      setVisible(true);
    };
    window.addEventListener(OPEN_EVENT, onOpen);
    return () => window.removeEventListener(OPEN_EVENT, onOpen);
  }, []);

  if (!visible) return null;

  const acceptAll = () => {
    saveConsent(true);
    setVisible(false);
  };
  const rejectAll = () => {
    saveConsent(false);
    setVisible(false);
  };
  const savePrefs = () => {
    saveConsent(analytics);
    setVisible(false);
  };

  return (
    <div
      className="fixed inset-x-0 bottom-0 z-[80] p-3 sm:p-4"
      role="dialog"
      aria-modal="false"
      aria-label="Preferințe cookies"
    >
      <div className="mx-auto max-w-4xl rounded-2xl border border-neutral-200 bg-white p-4 shadow-2xl dark:border-neutral-700 dark:bg-neutral-900 sm:p-5">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="text-sm text-neutral-700 dark:text-neutral-300">
            <p className="mb-1 font-semibold text-neutral-900 dark:text-neutral-100">
              Respectăm confidențialitatea ta
            </p>
            <p>
              Folosim cookies strict necesare pentru funcționare și, doar cu acordul tău, Google
              Analytics pentru statistici anonime de utilizare. Detalii în{' '}
              <a
                href="https://softfactura.ro/cookies"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary-600 underline hover:no-underline dark:text-primary-400"
              >
                Politica de cookies
              </a>
              .
            </p>
          </div>
          <div className="flex shrink-0 flex-col gap-2 sm:flex-row sm:items-center">
            <button type="button" onClick={rejectAll} className={btnSecondary}>
              Doar necesare
            </button>
            <button type="button" onClick={() => setCustomize((v) => !v)} className={btnGhost}>
              Personalizează
            </button>
            <button type="button" onClick={acceptAll} className={btnPrimary}>
              Accept toate
            </button>
          </div>
        </div>

        {customize && (
          <div className="mt-4 space-y-3 border-t border-neutral-100 pt-4 text-sm dark:border-neutral-800">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="font-medium text-neutral-900 dark:text-neutral-100">Strict necesare</p>
                <p className="text-neutral-500 dark:text-neutral-400">
                  Autentificare, sesiune, securitate. Mereu active.
                </p>
              </div>
              <input
                type="checkbox"
                checked
                readOnly
                disabled
                aria-label="Cookies strict necesare (mereu active)"
                className="mt-1 h-4 w-4 accent-primary"
              />
            </div>
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="font-medium text-neutral-900 dark:text-neutral-100">
                  Analytics (Google Analytics)
                </p>
                <p className="text-neutral-500 dark:text-neutral-400">
                  Statistici anonime despre cum este folosită aplicația.
                </p>
              </div>
              <input
                type="checkbox"
                checked={analytics}
                onChange={(e) => setAnalytics(e.target.checked)}
                aria-label="Cookies analytics"
                className="mt-1 h-4 w-4 accent-primary"
              />
            </div>
            <div className="flex justify-end">
              <button type="button" onClick={savePrefs} className={btnPrimary}>
                Salvează preferințele
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
